import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addressService,
  AddressServiceLanguage,
  AutoCompleteCitiesProps,
  AutoCompleteCitiesResponse,
  AutoCompleteStreetsProps,
  AutoCompleteStreetsResponse,
  GetCountriesProps,
  GetCountriesResponse,
  ResolveDutchAddressProps,
  ResolveDutchAddressResponse,
} from '../services/addressService';

type AddressService = {
  autoCompleteCities: (
    props: Omit<AutoCompleteCitiesProps, 'language'>
  ) => Promise<AutoCompleteCitiesResponse>;
  autoCompleteStreets: (
    props: Omit<AutoCompleteStreetsProps, 'language'>
  ) => Promise<AutoCompleteStreetsResponse>;
  autoCompleteCountries: (
    props: Omit<GetCountriesProps, 'language'>
  ) => Promise<GetCountriesResponse>;
  resolveDutchAddress: (props: ResolveDutchAddressProps) => Promise<ResolveDutchAddressResponse>;
};

export function useAddressService(): {
  addressServiceLanguage: AddressServiceLanguage;
  addressService: AddressService;
} {
  const { i18n } = useTranslation();

  const ui_locales = useMemo(() => {
    const browserLanguages = i18n.languages;
    const allowedLanguages = Object.values(AddressServiceLanguage);
    let ui_locales;

    /* eslint-disable no-labels */
    loop: for (const browserLanguage of browserLanguages) {
      for (const allowedLanguage of allowedLanguages) {
        if (browserLanguage.toLowerCase().includes(allowedLanguage.toLowerCase())) {
          ui_locales = allowedLanguage;
          break loop;
        }
      }
    }
    return ui_locales ?? AddressServiceLanguage.NL;
  }, [i18n.languages]);

  const supportedAddressLanguage = useMemo(() => {
    if (
      ui_locales === AddressServiceLanguage.DE ||
      ui_locales === AddressServiceLanguage.EN ||
      ui_locales === AddressServiceLanguage.PT
    ) {
      return AddressServiceLanguage.NL;
    }
    return ui_locales;
  }, [ui_locales]);

  const autoCompleteCities = useCallback(
    (props: Omit<AutoCompleteCitiesProps, 'language'>) => {
      return addressService.autoCompleteCities({
        ...props,
        language: supportedAddressLanguage,
      });
    },
    [supportedAddressLanguage]
  );

  const autoCompleteStreets = useCallback(
    (props: Omit<AutoCompleteStreetsProps, 'language'>) => {
      return addressService.autoCompleteStreets({
        ...props,
        language: supportedAddressLanguage,
      });
    },
    [supportedAddressLanguage]
  );

  const autoCompleteCountries = useCallback(
    (props: Omit<GetCountriesProps, 'language'>) => {
      return addressService.getCountries({
        ...props,
        language: ui_locales,
      });
    },
    [ui_locales]
  );

  const resolveDutchAddress = useCallback((props: ResolveDutchAddressProps) => {
    return addressService.resolveStreetFromPostcode({
      ...props,
    });
  }, []);

  return useMemo(
    () => ({
      addressServiceLanguage: supportedAddressLanguage,
      addressService: {
        autoCompleteCities,
        autoCompleteStreets,
        autoCompleteCountries,
        resolveDutchAddress,
      },
    }),
    [
      autoCompleteCities,
      autoCompleteStreets,
      autoCompleteCountries,
      supportedAddressLanguage,
      resolveDutchAddress,
    ]
  );
}
